/**
 * composition/types.ts
 *
 * Shared types for the agent composition module (Task 1.7).
 *
 * Defines the binding system, compile manifest schema, and the
 * resolved composition result that the runner consumes at session start.
 */

import type { AgentComposeItem, Binding } from "@skaile/workspaces/types/manifests";

// ---------------------------------------------------------------------------
// Re-export from types
// ---------------------------------------------------------------------------

export type { AgentComposeItem, Binding };

// ---------------------------------------------------------------------------
// Kind-to-binding defaults
// ---------------------------------------------------------------------------

/**
 * Default binding by asset kind.
 *
 * Per the spec: soul -> inline-snapshot, ruleset -> inline-live,
 * everything else (skill, knowledge, connector, mount, etc.) -> discoverable.
 */
const KIND_BINDING_DEFAULTS: Record<string, Binding> = {
  soul: "inline-snapshot",
  persona: "inline-snapshot",
  ruleset: "inline-live",
  skill: "discoverable",
  knowledge: "discoverable",
  connector: "discoverable",
  mount: "discoverable",
  prompt: "inline-live",
  flow: "discoverable",
  "mcp-server": "discoverable",
  contract: "inline-live",
};

/**
 * Resolve the effective binding for a composition item.
 * Uses explicit `binding` if set, otherwise falls back to kind default.
 *
 * @param item - A single entry from an agent manifest's `composes:` list.
 * @returns The effective {@link Binding} — explicit value when set, otherwise
 *   the kind-specific default (e.g. `soul` → `inline-snapshot`, `skill` → `discoverable`).
 * @docLink packages/runner/capabilities#resolve-binding
 */
export function resolveBinding(item: AgentComposeItem): Binding {
  if (item.binding) return item.binding;
  return KIND_BINDING_DEFAULTS[item.kind] ?? "discoverable";
}

// ---------------------------------------------------------------------------
// Compile manifest (written to <agent>/compile_manifest.json)
// ---------------------------------------------------------------------------

/**
 * Single entry in the agent's `compile_manifest.json`. Records the resolved
 * version, content hash, and effective binding for one composition item at
 * the time of the last `skaile rebuild` run.
 *
 * @docLink packages/runner/capabilities#compile-manifest-entry
 */
export interface CompileManifestEntry {
  /** Asset ref (e.g. '@skaile/sales-soul@1.2') */
  asset_ref: string;
  /** Resolved version (e.g. '1.2.3') */
  version: string | null;
  /** SHA256 of the resolved content */
  hash: string;
  /** Effective binding */
  binding: Binding;
  /** ISO timestamp when this entry was resolved */
  resolved_at: string;
}

/**
 * Root structure of `compile_manifest.json` written by `skaile rebuild`.
 * Tracks the prompt version counter and all resolved composition entries.
 * The runner reads this at session start to verify inline-snapshot content
 * against its stored hash.
 *
 * @docLink packages/runner/capabilities#compile-manifest
 */
export interface CompileManifest {
  /** Compile manifest schema version */
  schema_version: 1;
  /** Prompt version — bumped on every rebuild */
  prompt_version: number;
  /** Entries for each composition item */
  entries: CompileManifestEntry[];
  /** ISO timestamp of the last rebuild */
  compiled_at: string;
}

// ---------------------------------------------------------------------------
// Resolved composition result (consumed by runner at session start)
// ---------------------------------------------------------------------------

/**
 * A resolved inline content block for system prompt injection.
 *
 * @docLink packages/runner/capabilities#inline-block
 */
export interface InlineBlock {
  /** Source ref for debugging */
  ref: string;
  /** Asset kind */
  kind: string;
  /** The text content to inject */
  content: string;
  /** Binding mode that produced this block */
  binding: "inline-live" | "inline-snapshot";
}

/**
 * A resolved discoverable item (becomes a tool-list entry).
 *
 * @docLink packages/runner/capabilities#discoverable-entry
 */
export interface DiscoverableEntry {
  /** Source ref */
  ref: string;
  /** Asset kind */
  kind: string;
  /** Tool name for capability registry */
  name: string;
  /** One-line description */
  description: string;
  /** Full content (loaded on demand when the tool is invoked) */
  content: string;
}

/**
 * The full result of resolving an agent's composes: field.
 *
 * @docLink packages/runner/capabilities#resolved-composition
 */
export interface ResolvedComposition {
  /** Blocks to inline into the system prompt (inline-live + inline-snapshot) */
  inlineBlocks: InlineBlock[];
  /** Items to register as discoverable capabilities */
  discoverableEntries: DiscoverableEntry[];
  /** Warnings (non-fatal — e.g. missing assets) */
  warnings: string[];
}
